/**
 * DataTableEnhanced - Reusable TanStack React Table component for EBProjects
 *
 * Inspired by EBTimeEntry's DataTableEnhanced pattern, this component provides
 * a clean separation between data management (parent) and table rendering (this component).
 *
 * Architecture:
 *   Page Component (data fetching, filtering, pagination state)
 *     └─ DataTableEnhanced (rendering, sort UI, column visibility)
 *
 * Supports:
 *   - Multi-column sorting with priority badges (controlled by parent)
 *   - Column visibility bridging with ColumnDefinition type
 *   - Loading skeleton state
 *   - Customisable empty state messages
 *   - Row click handling
 *   - TanStack ColumnDef for type-safe cell renderers
 */

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  VisibilityState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { FloatingHorizontalScrollbar } from '@/components/ui/floating-horizontal-scrollbar';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────────

/** A single column sort entry used for multi-column sorting */
export interface ColumnSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

/** Resolved sort state for a specific column */
export interface ColumnSortState {
  direction: 'asc' | 'desc';
  priority: number;
}

export interface DataTableEnhancedProps<TData, TValue> {
  /** TanStack column definitions with cell renderers */
  columns: ColumnDef<TData, TValue>[];
  /** Data rows to render (already filtered/sorted/paginated by parent if needed) */
  data: TData[];

  // ── Column Visibility ──────────────────────────────────────────────
  /** Column visibility state - keys are column IDs, values indicate visibility */
  columnVisibility?: VisibilityState;
  /** Callback when column visibility changes */
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;

  // ── Multi-Column Sorting ───────────────────────────────────────────
  /** Current multi-column sort state (ordered by priority) */
  columnSorts?: ColumnSort[];
  /** Callback when a column header is clicked to toggle sorting */
  onColumnSortChange?: (columnSorts: ColumnSort[]) => void;

  // ── UI States ──────────────────────────────────────────────────────
  /** Show skeleton loading rows */
  loading?: boolean;
  /** Number of skeleton rows to show when loading */
  loadingRowCount?: number;
  /** Show a fade/disabled overlay during page transitions */
  isTransitioning?: boolean;
  /** Custom empty state content */
  emptyMessage?: React.ReactNode;

  // ── Row Interaction ────────────────────────────────────────────────
  /** Callback when a data row is clicked */
  onRowClick?: (row: TData) => void;
  /** Function to generate a unique key for each row */
  getRowId?: (row: TData) => string;

  // ── Style ──────────────────────────────────────────────────────────
  /** Additional class name for the table container */
  className?: string;
  /** data-tour attribute for the table element (for guided tours) */
  dataTour?: string;

  // ── Pagination ─────────────────────────────────────────────────────
  /** Optional pagination component rendered below the table */
  paginationContent?: React.ReactNode;
  /** Whether the pagination footer should be sticky (default: true) */
  stickyPagination?: boolean;

  // ── Custom Row / Footer ────────────────────────────────────────────
  /**
   * Optional custom row renderer. Return a `<tr>` element to override default
   * row rendering for a specific row (e.g. group-header pseudo-rows), or
   * return null to fall back to the default rendering.
   */
  customRowRenderer?: (row: import('@tanstack/react-table').Row<TData>, defaultRow: React.ReactNode) => React.ReactNode | null;
  /**
   * Optional content rendered as a `<tbody>` section ABOVE the data rows (e.g. synthetic group rows).
   * Wrap your content in `<tr>` / `<td>` elements.
   */
  headerContent?: React.ReactNode;
  /**
   * Optional footer content rendered as a `<tfoot>` inside the table (e.g. totals row).
   * Wrap your content in `<tr>` / `<td>` elements.
   */
  footerContent?: React.ReactNode;
  /**
   * When true, the `<TableHeader>` is hidden. Useful when rendering multiple
   * grid instances in a grouped view (only the first should show headers).
   */
  hideHeader?: boolean;
  /**
   * Optional rows appended to the end of `<TableBody>` (e.g. subtotal rows in grouped view).
   * Wrap content in `<tr>` / `<td>` elements.
   */
  appendedRows?: React.ReactNode;
}

// ── Helpers ────────────────────────────────────────────────────────────

/**
 * Cycle a column through multi-sort states: none → asc → desc → remove.
 * Matches the existing EBProjects sorting behaviour.
 */
export function cycleColumnSort(
  prev: ColumnSort[],
  columnId: string,
): ColumnSort[] {
  const existingIndex = prev.findIndex((s) => s.columnId === columnId);
  if (existingIndex === -1) {
    // Not sorted yet → add as asc
    return [...prev, { columnId, direction: 'asc' }];
  }
  const existing = prev[existingIndex];
  if (existing.direction === 'asc') {
    // asc → desc
    const next = [...prev];
    next[existingIndex] = { columnId, direction: 'desc' };
    return next;
  }
  // desc → remove
  return prev.filter((s) => s.columnId !== columnId);
}

/**
 * Get the sort state for a specific column from the multi-sort array.
 */
export function getColumnSortState(
  columnSorts: ColumnSort[],
  columnId: string,
): ColumnSortState | null {
  const idx = columnSorts.findIndex((s) => s.columnId === columnId);
  if (idx === -1) return null;
  return { direction: columnSorts[idx].direction, priority: idx + 1 };
}

// ── Component ──────────────────────────────────────────────────────────

export function DataTableEnhanced<TData, TValue>({
  columns,
  data,
  columnVisibility = {},
  onColumnVisibilityChange,
  columnSorts = [],
  onColumnSortChange,
  loading = false,
  loadingRowCount = 10,
  isTransitioning = false,
  emptyMessage,
  onRowClick,
  getRowId,
  className,
  dataTour,
  paginationContent,
  stickyPagination = false,
  headerContent,
  customRowRenderer,
  footerContent,
  hideHeader = false,
  appendedRows,
}: DataTableEnhancedProps<TData, TValue>) {
  // TanStack Table instance — used purely for rendering via flexRender.
  // Sorting and pagination are managed externally by the parent component.
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Column visibility is controlled
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnVisibility) : updater;
      onColumnVisibilityChange?.(next);
    },
    state: {
      columnVisibility,
    },
    // We handle sorting externally (multi-column sort with priority badges)
    manualSorting: true,
    // We handle pagination externally
    manualPagination: true,
    // Row ID
    getRowId: getRowId
      ? (row) => getRowId(row)
      : undefined,
  });

  const handleHeaderSort = React.useCallback(
    (columnId: string, shiftKey: boolean) => {
      if (!onColumnSortChange) return;
      if (shiftKey) {
        // Shift+click: add/cycle this column within the existing multi-sort array
        onColumnSortChange(cycleColumnSort(columnSorts, columnId));
      } else {
        // Plain click: sort by this column only (replace any existing sorts)
        const existing = columnSorts.find((s) => s.columnId === columnId);
        if (!existing) {
          onColumnSortChange([{ columnId, direction: 'asc' }]);
        } else if (existing.direction === 'asc') {
          onColumnSortChange([{ columnId, direction: 'desc' }]);
        } else {
          // desc → clear sort entirely
          onColumnSortChange([]);
        }
      }
    },
    [columnSorts, onColumnSortChange],
  );

  const visibleColumnCount = table.getVisibleFlatColumns().length;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-col min-h-0">
      {/* Table */}
      <div
        className={cn(
          'rounded-md border relative overflow-hidden min-h-0',
          isTransitioning &&
            'opacity-50 pointer-events-none transition-opacity',
          !isTransitioning && 'transition-opacity',
          className,
        )}
      >
        <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-visible">
        <Table data-tour={dataTour} className="text-xs w-max min-w-full" disableScroll>
          {!hideHeader && <TableHeader divided className="sticky top-0 z-10 bg-card">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sortState = getColumnSortState(
                    columnSorts,
                    header.column.id,
                  );
                  const canSort = onColumnSortChange !== undefined && header.column.columnDef.enableSorting !== false;
                  const meta = header.column.columnDef.meta as { align?: string; width?: number | string } | undefined;
                  const align = meta?.align ?? 'left';
                  const colWidth = meta?.width;

                  return (
                    <TableHead
                      key={header.id}
                      style={colWidth ? { width: typeof colWidth === 'number' ? `${colWidth}px` : colWidth, minWidth: typeof colWidth === 'number' ? `${colWidth}px` : colWidth } : undefined}
                      className={cn(
                        'bg-card select-none transition-colors',
                        align === 'right' && 'text-right',
                        align === 'center' && 'text-center',
                        canSort && 'cursor-pointer hover:bg-accent/50',
                      )}
                      onClick={(e) => canSort && handleHeaderSort(header.column.id, e.shiftKey)}
                      title={canSort ? 'Click to sort · Shift+click to add to multi-sort' : undefined}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            'flex items-center gap-1',
                            align === 'right' && 'justify-end',
                            align === 'center' && 'justify-center',
                          )}
                        >
                          {header.column.id === '__select__' ? (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )
                          ) : (
                            <span className="font-semibold">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </span>
                          )}
                          {canSort && (
                            <span className="inline-flex items-center gap-0.5 ml-0.5">
                              {sortState ? (
                                <>
                                  {sortState.direction === 'asc' ? (
                                    <ArrowUp className="h-3.5 w-3.5 text-primary" />
                                  ) : (
                                    <ArrowDown className="h-3.5 w-3.5 text-primary" />
                                  )}
                                  {columnSorts.length > 1 && (
                                    <span className="text-[10px] font-semibold text-primary min-w-[14px] h-[14px] rounded-full bg-primary/10 inline-flex items-center justify-center">
                                      {sortState.priority}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/40" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>}

          {headerContent && (
            <tbody>{headerContent}</tbody>
          )}

          <TableBody>
            {loading ? (
              // Skeleton loading state
              Array.from({ length: loadingRowCount }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {table.getVisibleFlatColumns().map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  className="text-center text-muted-foreground py-12"
                >
                  {emptyMessage ?? (
                    <div className="space-y-2">
                      <p className="text-lg font-medium">No results found</p>
                      <p className="text-sm">
                        Try adjusting your filters or search term
                      </p>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              // Data rows
              table.getRowModel().rows.map((row) => {
                // Allow parent to override rendering for specific rows (e.g. group headers)
                const defaultRowEl = (
                  <TableRow
                    key={row.id}
                    className={cn(
                      'even:bg-white odd:bg-neutral-50',
                      onRowClick ? 'hover:bg-neutral-100 cursor-pointer' : 'odd:hover:bg-neutral-50 even:hover:bg-white',
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const align =
                        (cell.column.columnDef.meta as { align?: string })
                          ?.align ?? 'left';
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            align === 'right' && 'text-right',
                            align === 'center' && 'text-center',
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
                if (customRowRenderer) {
                  const custom = customRowRenderer(row, defaultRowEl);
                  if (custom !== null) return custom;
                }
                return defaultRowEl;
              })
            )}
            {appendedRows}
          </TableBody>
          {footerContent && (
            <tfoot>{footerContent}</tfoot>
          )}
        </Table>
        </div>
      </div>

      {/* Keeps horizontal scrolling reachable when the table runs past the viewport */}
      <FloatingHorizontalScrollbar targetRef={scrollContainerRef} />

      {/* Pagination (rendered by parent, placed here for layout) */}
      {paginationContent && (
        <div className={cn("bg-card py-3 px-6 flex items-center w-full shrink-0", stickyPagination && "sticky bottom-0 z-10")}>
          <div className="w-full">{paginationContent}</div>
        </div>
      )}
    </div>
  );
}

// ── Utility: Bridge ColumnDefinition[] to TanStack VisibilityState ────

import type { ColumnDefinition } from '@/components/column-configurator-drawer';

/**
 * Convert an array of ColumnDefinition (from column-configurator-drawer)
 * to a TanStack VisibilityState record.
 */
export function columnDefinitionsToVisibility(
  columns: ColumnDefinition[],
): VisibilityState {
  const visibility: VisibilityState = {};
  for (const col of columns) {
    visibility[col.id] = col.visible;
  }
  return visibility;
}

/**
 * Get the ordered list of visible column IDs from ColumnDefinition[].
 */
export function getVisibleColumnIds(columns: ColumnDefinition[]): string[] {
  return columns
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order)
    .map((c) => c.id);
}

// Re-export useful TanStack types for consumers
export type { ColumnDef, VisibilityState } from '@tanstack/react-table';
